import * as React from 'react';
import StyledModal from '../../../Components/StyledModal';
import type { SelectMap, SelectMapGroup } from '../../../Utilities/SelectMap';

export interface MultiSelectModalProps {
  modalTitle?: string;
  modalSaveButtonLabel?: string;

  buttonSelectLabel?: string;
  buttonEditLabel?: string;

  onChange?: (value: string[]) => void;

  options: SelectMapGroup[] | SelectMap[];
  disabled?: boolean;

  value: string[];

  search?: boolean;
}

export const MultiSelectModal = (props: MultiSelectModalProps): React.JSX.Element => {
  const [modalSaveButtonLabel] = React.useState<string>(
    props.modalSaveButtonLabel || 'Save Changes'
  );
  const [modalTitle] = React.useState<string>(props.modalTitle || 'Select Options');
  const [buttonSelectLabel] = React.useState<string>(props.buttonSelectLabel || 'Select');
  const [buttonEditLabel] = React.useState<string>(props.buttonEditLabel || 'Edit Selection');

  const [options] = React.useState<any>(props.options || null);

  const [showModal, setShowModal] = React.useState<boolean>(false);

  const [value, setValue] = React.useState<string[]>(props.value);
  const [valueLive, setValueLive] = React.useState<string[]>(props.value);

  const [query, setQuery] = React.useState<string>('');

  const isGrouped = React.useMemo<boolean>(() => {
    return Array.isArray(options) && options.length > 0 && 'items' in options[0];
  }, [options]);

  /** NEW: filtered view of options based on query (case-insensitive) */
  const filtered = React.useMemo(() => {
    if (!options) return options;

    const q = query.trim().toLowerCase();
    if (!props.search || q.length === 0) return options;

    if (isGrouped) {
      // Filter items inside each group; drop groups that end up empty
      return (options as SelectMapGroup[])
        .map((g) => ({
          ...g,
          items: g.items.filter((it) => String(it.label).toLowerCase().includes(q)),
        }))
        .filter((g) => g.items.length > 0);
    } else {
      // Flat list
      return (options as SelectMap[]).filter((it) => String(it.label).toLowerCase().includes(q));
    }
  }, [options, isGrouped, query, props.search]);

  const toggleValue = (newSingleValue: string) => {
    const newValue = toggleArrayValue(value, newSingleValue);

    setValue(newValue);
  };

  const toggleArrayValue = (array: string[], value: string): string[] => {
    const newArray = array ? [...array] : [];
    const existingItem = newArray.indexOf(value);

    if (existingItem > -1) {
      newArray.splice(existingItem, 1);
    } else {
      newArray.push(value);
    }

    return newArray;
  };

  const displayModal = () => {
    setShowModal(true);
  };

  const hideModal = () => {
    setShowModal(false);
  };

  React.useEffect(() => {
    setValue(props.value);
    setValueLive(props.value);
  }, [props.value]);

  // Flatten helper for badge display lookup
  const allFlatItems: SelectMap[] = React.useMemo(() => {
    if (!options) return [];
    if (isGrouped) {
      return (options as SelectMapGroup[]).flatMap((g) => g.items);
    }
    return options as SelectMap[];
  }, [options, isGrouped]);

  return (
    <>
      <StyledModal
        size="m"
        title={modalTitle}
        footerCloseButton={true}
        footerButtons={
          <div
            onClick={(e) => {
              if (props.onChange) {
                props.onChange(value);
              }

              setValueLive([...value]);
              hideModal();
            }}
            className="btn btn-success"
          >
            {modalSaveButtonLabel}
          </div>
        }
        show={showModal}
        onClose={hideModal}
      >
        {/* NEW: search bar */}
        {props.search && (
          <div className="mb-2">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        )}

        {/* Grouped */}
        {filtered && isGrouped && (
          <>
            {(filtered as SelectMapGroup[]).length === 0 && (
              <div className="text-muted small">No matches.</div>
            )}

            {(filtered as SelectMapGroup[]).map((itemGroup, gi) => (
              <div key={`grp-${gi}`} className="mb-2">
                <label className="mb-0">{itemGroup.label}</label>
                <div className="mb-2">
                  {itemGroup.items.map((item, ii) => {
                    const selected = Array.isArray(value) && value.includes(item.value);
                    const classList = [
                      'badge',
                      'font-weight-normal',
                      'border',
                      'mr-1',
                      ...(selected ? ['bg-success', 'border-success', 'text-white'] : []),
                    ];
                    return (
                      <button
                        key={`g-${gi}-i-${ii}-${item.value}`}
                        onClick={() => toggleValue(item.value)}
                        className={classList.join(' ')}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Flat */}
        {filtered && !isGrouped && (
          <div className="mb-2">
            {(filtered as SelectMap[]).length === 0 && (
              <div className="text-muted small">No matches.</div>
            )}
            {(filtered as SelectMap[]).map((item, i) => {
              const selected = Array.isArray(value) && value.includes(item.value);
              const classList = [
                'badge',
                'font-weight-normal',
                'border',
                'mr-1',
                ...(selected ? ['bg-success', 'border-success', 'text-white'] : []),
              ];
              return (
                <button
                  key={`i-${i}-${item.value}`}
                  onClick={() => toggleValue(item.value)}
                  className={classList.join(' ')}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        )}
      </StyledModal>

      <div>
        {options && valueLive && valueLive.length > 0 && (
          <div className="mb-1">
            {valueLive.map((item) => {
              const classList = [
                'badge',
                'font-weight-normal',
                'border',
                'mr-1',
                'bg-success',
                'border-success',
                'text-white',
              ];

              // TODO: Make this more efficient
              let itemLabel;

              if (options[0] && options[0].items) {
                let items = [];
                options.forEach((itemSingle) => {
                  items = items.concat(itemSingle.items);
                });

                itemLabel = items.find((option) => option.value === item);
              } else {
                itemLabel = options.find((option) => option.value === item);
              }

              return (
                <div key={itemLabel.label} role="button" className={classList.join(' ')}>
                  {itemLabel.label}
                </div>
              );
            })}
          </div>
        )}
        {(!valueLive || valueLive.length === 0) && (
          <div className="mb-1">
            <div
              role="button"
              className={[
                'badge',
                'font-weight-normal',
                'border',
                'mr-1',
                'bg-secondary',
                'border-secondary',
                'text-white',
              ].join(' ')}
            >
              None selected
            </div>
          </div>
        )}
        {options !== null && (
          <button
            disabled={props.disabled}
            onClick={props.disabled ? null : displayModal}
            className="btn btn-sm border border-secondary"
          >
            {valueLive && valueLive.length > 0 ? buttonEditLabel : buttonSelectLabel}
          </button>
        )}
      </div>
    </>
  );
};
